// DragDropFileUpload.jsx
import { useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import uploadedfiles from "../../assets/images/Upload.svg";
import xclose from "../../assets/images/Xclose.svg";
import multiplefile from "../../assets/images/add_to_photos.svg";
import useLanguageTranslate from "../../hooks/useLanguageTranslate";
import AcceptedFilesNote, { extensionsFor } from "./acceptedFilesNote";
import useScreenResolution from "../../hooks/useScreenResolution";
import { removeClaimFile, storeClaimFile } from "../../utils/claimFilesStorage";

const DragDropFileUpload = ({
  uploadedFiles = [],
  setUploadedFiles = () => {},
  name,
  label,
  onUploadComplete,
  multiple = true,
  toggleFileUpload,
  uploadDocFunc,
  maxFiles = 5,
  // What the drop area takes — and tells the customer it takes. The default is
  // what every consumer of this component feeds downstream — the files API and
  // Vista's OCR both read images and PDFs — and a caller with a narrower slot
  // passes its own list (the claim page's Accident Photos slot: Vista does not
  // read a PDF there). The list is enforced on both inputs, not only shown,
  // so the note under the drop area cannot promise a type the slot refuses.
  acceptedTypes = ["image/*", "application/pdf"],
  // Called with the files that survive a delete, the slot's name, and the file
  // that went. Optional: only the claim upload page listens, to take the
  // deleted document out of Vista's session and re-render its findings.
  onFileRemoved = () => {},
}) => {
  const translate = useLanguageTranslate();
  const [progress, setProgress] = useState({});
  const fileInputRef = useRef(null);
  const { t } = useTranslation();
  const screenResolution = useScreenResolution();

  const generateUniqueIdentifier = () => {
    return (
      Date.now().toString(36) + "-" + Math.random().toString(36).substring(2, 8)
    );
  };

  // A file the slot does not take. The OS picker filters by the input's
  // `accept`, but a drop bypasses it and some pickers ignore it, so the type
  // is checked here as well. "image/*"-style entries match by prefix.
  const isAcceptedType = (file) => {
    const type = String(file?.type || "").toLowerCase();
    return acceptedTypes.some((accepted) => {
      const wanted = String(accepted).toLowerCase();
      if (wanted.endsWith("/*")) return type.startsWith(wanted.slice(0, -1));
      return type === wanted;
    });
  };

  const rejectUnsupported = (file) => {
    toast.error(
      translate(
        `"${file.name}" is not a supported file type. Accepted: ${extensionsFor(acceptedTypes).join(", ")}.`,
        `"${file.name}" ليس نوع ملف مدعومًا. الأنواع المقبولة: ${extensionsFor(acceptedTypes).join("، ")}.`
      )
    );
  };

  const processFiles = (input) => {
    const offered = Array.isArray(input) ? input : Array.from(input.files);
    const newFiles = offered.filter(isAcceptedType);
    offered.filter((file) => !isAcceptedType(file)).forEach(rejectUnsupported);
    if (!newFiles.length) return;
    const totalFiles = uploadedFiles.length + newFiles.length;

    if (totalFiles > maxFiles) {
      alert(`You can only upload up to ${maxFiles} files.`);
      return;
    }

    const filesWithPreview = newFiles.map((file) => {
      const fileKey = generateUniqueIdentifier();

      // Store the original File blob in memory storage for Vista API
      storeClaimFile(fileKey, file);

      return Object.assign(file, {
        preview: URL.createObjectURL(file),
        _key: fileKey,
        uploaded: false,
      });
    });

    const updatedFiles = [...uploadedFiles, ...filesWithPreview];
    console.log("[processFiles] Updated files:", updatedFiles);
    setUploadedFiles(updatedFiles);
    toggleFileUpload(updatedFiles, name);

    filesWithPreview.forEach((file) => {
      handleFileUpload(file);
    });
  };

  const onDrop = (acceptedFiles) => {
    console.log("[onDrop] Files dropped:", acceptedFiles);
    processFiles(acceptedFiles);
  };

  const handleFileUpload = async (file) => {
    let uploadProgress = 0;
    let link = null;

    const startUploadInterval = () => {
      return setInterval(async () => {
        uploadProgress += 10;

        setProgress((prevProgress) => ({
          ...prevProgress,
          [file._key]: uploadProgress,
        }));

        if (uploadProgress == 10 && !link) {
          clearInterval(uploadInterval);
          console.log("[handleFileUpload] Uploading file:", file.name);
          link = await uploadDocFunc(file, name);
          console.log("[handleFileUpload] Upload link:", link);

          file.preview = link || file.preview;
          file.uploaded = true;
          console.log("[handleFileUpload] File uploaded with preview:", file);

          uploadInterval = startUploadInterval();
        }

        if (uploadProgress >= 100) {
          clearInterval(uploadInterval);
          onUploadComplete && onUploadComplete(file, name);
          console.log("[handleFileUpload] Upload complete for:", file.name);
        }
      }, 300);
    };

    let uploadInterval = startUploadInterval();
  };

  const handleDelete = (fileToDelete) => {
    console.log("[handleDelete] Deleting file:", fileToDelete);

    // Remove from claim files storage
    if (fileToDelete._key) {
      removeClaimFile(fileToDelete._key);
    }

    const newFileList = uploadedFiles.filter(
      (file) => file._key != fileToDelete._key
    );
    setUploadedFiles(newFileList);
    toggleFileUpload(newFileList, name);
    onFileRemoved(newFileList, name, fileToDelete);

    setProgress((prevProgress) => {
      const updatedProgress = { ...prevProgress };
      delete updatedProgress[fileToDelete._key];
      return updatedProgress;
    });
  };

  const handleFileChange = (event) => {
    console.log("[handleFileChange] Files selected:", event.target.files);
    processFiles(event.target);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    // The dropzone filters by `accept` before onDrop sees the files, so the
    // ones it turned away are named here rather than dropped silently.
    onDropRejected: (rejections) =>
      rejections.forEach((rejection) => rejectUnsupported(rejection.file)),
    multiple,
    // react-dropzone wants { mime: [extensions] }; the extensions are left to
    // the MIME match, which is what the type check above does too.
    accept: Object.fromEntries(acceptedTypes.map((type) => [type, []])),
  });

  return (
    <div className="file-upload">
      <h3 className="mb-2 text-[#48535B] text-base not-italic font-normal leading-[26px] tracking-[-0.16px] mt-6">
        {label}
      </h3>
      {!screenResolution.mobile ? (
        <div
          {...getRootProps({
            // min-h, not h: the accepted-formats line has to be able to wrap
            // without being clipped by a fixed height.
            className:
              "dropzone min-h-[164px] justify-center items-center bg-[#F6F8F9] px-4 py-8 rounded-md text-center",
            disabled: uploadedFiles.length >= maxFiles,
          })}
        >
          <input
            {...getInputProps()}
            disabled={uploadedFiles.length >= maxFiles}
          />
          <p className="justify-center flex">
            <img src={uploadedfiles} className="items-center" alt="upload" />
          </p>
          <p className="text-[#252C32] text-center text-sm not-italic font-normal leading-6 mb-2">
            {translate("Drag and drop files here", "اسحب الملفات هنا")}
          </p>
          <button
            type="button"
            className="items-center gap-2.5 border bg-white px-4 py-2 rounded-md text-[#48535B] text-center text-sm not-italic font-semibold leading-6 tracking-[-0.084px]"
            disabled={uploadedFiles.length >= maxFiles}
          >
            {translate("Browse", "تصفح")}
          </button>
          <AcceptedFilesNote types={acceptedTypes} />
        </div>
      ) : (
        <div className="flex flex-col space-y-2">
          <div className="max767:block hidden relative">
            <div
              className={`flex fileInput w-full min-h-[164px] mb-4 bg-[#F6F8F9]`}
            >
              <input
                type="file"
                className="opacity-0 flex h-[164px] justify-center items-center shrink-0 self-stretch w-full rounded-md z-[99] absolute"
                multiple
                accept={acceptedTypes.join(",")}
                onChange={handleFileChange}
                ref={fileInputRef}
                disabled={uploadedFiles.length >= maxFiles}
              />
              <div className="flex flex-col items-center gap-1 relative z-0 mx-auto my-0 top-[60px] inset-x-0">
                <p>
                  <img src={multiplefile} alt="Select multiple files" />
                </p>
                <p>
                  <label>{translate("Add file or photo", "أضف ملف أو صورة")}</label>
                </p>
                <AcceptedFilesNote types={acceptedTypes} className="mt-0 px-4" />
              </div>
            </div>
          </div>
        </div>
      )}

      {uploadedFiles.length > 0 && (
        <div className="uploaded-files mt-4 flex items-start gap-2 self-stretch py-2 rounded-md max767:mt-0 max767:grid flex-col">
          {uploadedFiles.map((file) => (
            <div
              key={file._key}
              className="flex file-item w-full bg-[#F6F8F9] pl-4 pr-2 py-2 mb-1 rounded-md"
            >
              <div className="flex justify-between items-center w-full">
                <span className="text-[#252C32] text-sm not-italic font-normal leading-6">
                  {file.name}
                </span>

                {progress[file._key] == 100 && (
                  <button
                    className="ml-2 px-2 py-1 text-white rounded"
                    onClick={() => handleDelete(file)}
                  >
                    <img src={xclose} alt="close icon" />
                  </button>
                )}
              </div>

              {progress[file._key] < 100 && (
                <div className="relative mt-2 h-2 w-full bg-[#AAE2F7] rounded">
                  <div
                    className="absolute top-0 h-full bg-[#00A9E7] rounded"
                    style={{ width: `${progress[file._key]}%` }}
                  ></div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DragDropFileUpload;
